import React from 'react';
import SanityBackgroundMedia, {
    type SanityBackgroundMediaProps,
    type MediaItemComponent,
} from '@fogia/core-web/components/SanityMedia/SanityBackgroundMedia';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import type { ValidBreakpointLabels } from '@fogia/core-web/config/breakpoints';

export interface MediaItemResponsiveComponent {
    type: 'mediaItemResponsiveComponent';
    portrait?: MediaItemComponent;
    landscape?: MediaItemComponent;
}
export interface SanityBackgroundMediaSwitchProps extends Omit<SanityBackgroundMediaProps, 'media'> {
    /** Responsive media item with portrait and/or landscape variants */
    media: MediaItemResponsiveComponent;
    /** Breakpoint label (e.g., 'tablet.lg') for media queries. Defaults to 'tablet.lg'. Only used when mode is 'auto' */
    breakpoint?: ValidBreakpointLabels;
    /** Display mode. 'auto' (default) uses breakpoint hook – only the active variant is in the DOM (one media loads). 'portrait'/'landscape' forces a specific variant */
    mode?: 'auto' | 'portrait' | 'landscape';
    /** React children to render on top of the media */
    children?: React.ReactNode;
}

/**
 * Component that switches between portrait and landscape media based on viewport width or explicit mode.
 *
 * By default, this component uses useBreakpoint so only the active variant is in the DOM (one media loads).
 * Portrait is shown below the breakpoint, landscape when the viewport is at or above the specified breakpoint.
 *
 * Use the `mode` prop to force a specific variant (e.g. portrait in a 2 column layout).
 *
 * If one variant is missing, the available variant is used for both (fallback behavior).
 *
 * **Display Modes:**
 * - `'auto'` (default): Uses useBreakpoint({ from: breakpoint }) – only the matching variant is rendered.
 * - `'portrait'`: Always shows portrait variant.
 * - `'landscape'`: Always shows landscape variant.
 *
 * @example
 * ```tsx
 * // Default: above tablet.lg shows landscape, below shows portrait
 * <SanityBackgroundMediaSwitch media={mediaItemResponsiveComponent} />
 *
 * // Explicit breakpoint
 * <SanityBackgroundMediaSwitch media={mediaItemResponsiveComponent} breakpoint="desktop.sm" />
 *
 * // Force portrait
 * <SanityBackgroundMediaSwitch media={mediaItemResponsiveComponent} mode="portrait" />
 * ```
 *
 * @returns Rendered responsive media component or null if media is invalid
 */
const SanityBackgroundMediaSwitch: React.FC<SanityBackgroundMediaSwitchProps> = ({
    media,
    breakpoint = 'tablet.lg',
    mode = 'auto',
    children,
    ...rest
}) => {
    const isAboveBreakpoint = useBreakpoint({ from: breakpoint });

    if (!media || media.type !== 'mediaItemResponsiveComponent') {
        return null;
    }

    const { portrait, landscape } = media;
    /**
     * Helper function to validate if a media item is valid
     * Checks that the media has mediaType and the corresponding image or video property
     */
    const isValidMedia = (media?: MediaItemComponent): media is MediaItemComponent => {
        if (!media?.mediaType) {
            return false;
        }

        return media.mediaType === 'image' ? Boolean(media.image) : Boolean(media.video);
    };

    const portraitMedia = isValidMedia(portrait) ? portrait : null;
    const landscapeMedia = isValidMedia(landscape) ? landscape : null;

    const finalPortraitMedia = portraitMedia || landscapeMedia;
    const finalLandscapeMedia = landscapeMedia || portraitMedia;

    if (!finalPortraitMedia) {
        return null;
    }

    // When mode is portrait or landscape, use fallback if needed
    if (mode === 'portrait') {
        return (
            <SanityBackgroundMedia media={finalPortraitMedia} {...rest}>
                {children}
            </SanityBackgroundMedia>
        );
    }

    if (mode === 'landscape') {
        return (
            <SanityBackgroundMedia media={finalLandscapeMedia!} {...rest}>
                {children}
            </SanityBackgroundMedia>
        );
    }

    // Two distinct variants: render only the one that matches the viewport (useBreakpoint).
    // Only one media is in the DOM, so only one loads – no double weight.
    if (portraitMedia && landscapeMedia) {
        const activeMedia = isAboveBreakpoint ? landscapeMedia : portraitMedia;
        return (
            <SanityBackgroundMedia media={activeMedia} {...rest}>
                {children}
            </SanityBackgroundMedia>
        );
    }

    // Fallback: only one variant exists, render it without media queries
    return (
        <SanityBackgroundMedia media={finalPortraitMedia} {...rest}>
            {children}
        </SanityBackgroundMedia>
    );
};

export default SanityBackgroundMediaSwitch;
